import { useEffect, useState } from "react";
import { api, errMsg, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { MessageCircle, Building, Landmark, MailWarning, Phone } from "lucide-react";

const CHANNELS = [
  { key: "secretaria", label: "Secretaría", icon: Building, desc: "Canal administrativo: matrículas, pagos, documentación." },
  { key: "direccion", label: "Dirección", icon: Landmark, desc: "Canal institucional para asuntos de dirección." },
  { key: "queja", label: "Quejas y Sugerencias", icon: MailWarning, desc: "Puedes enviarla de forma identificada o anónima." },
];

export default function Communication() {
  const { user } = useAuth();
  const [teacher, setTeacher] = useState(null);
  const [channel, setChannel] = useState("secretaria");
  const [content, setContent] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [messages, setMessages] = useState([]);
  const [waText, setWaText] = useState("");
  const isStudent = user.role === "ALUMNO";

  const load = () => api.get("/messages").then((r) => setMessages(r.data));
  useEffect(() => {
    load();
    if (isStudent) api.get("/my-teacher").then((r) => setTeacher(r.data.teacher)).catch(() => {});
  }, []);

  const send = async () => {
    if (!content.trim()) return;
    try {
      await api.post("/messages", { channel, content, anonymous: channel === "queja" ? anonymous : false });
      toast.success("Mensaje enviado");
      setContent(""); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const askTeacher = () => {
    const phone = teacher?.phone?.replace(/\D/g, "");
    if (!phone) { toast.error("Tu profesor todavía no tiene WhatsApp configurado. Contacta con secretaría."); return; }
    const msg = encodeURIComponent(waText || "Hola, soy " + user.name + ", alumno/a de Natur Academy. Tengo una consulta:");
    window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
  };

  return (
    <div className="fade-up" data-testid="communication-page">
      <h1 className="text-3xl font-semibold tracking-tight mb-1">Comunicación</h1>
      <p className="text-neutral-500 mb-8">Contacta con tu profesor, secretaría o dirección.</p>

      {isStudent && (
        <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6 mb-8 dakota-glow" data-testid="teacher-channel">
          <h2 className="font-semibold flex items-center gap-2 mb-1"><MessageCircle size={17} className="text-green-600" /> Profesor</h2>
          <p className="text-sm text-neutral-500 mb-4">
            {teacher ? `Tu profesor asignado es ${teacher.name}. Tu consulta se abrirá en su WhatsApp.` : "Todavía no tienes profesor asignado."}
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Textarea data-testid="teacher-message-input" rows={1} placeholder="Escribe tu consulta (texto)…" value={waText}
                      onChange={(e) => setWaText(e.target.value)} className="flex-1" />
            <Button data-testid="ask-teacher-btn" onClick={askTeacher} disabled={!teacher}
                    className="rounded-full bg-green-600 hover:bg-green-700 text-white">
              <Phone size={15} className="mr-1.5" /> Preguntar al profesor
            </Button>
          </div>
          <p className="text-xs text-neutral-400 mt-2">También puedes enviarle un audio directamente desde WhatsApp una vez abierto el chat.</p>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6 h-fit">
          <h2 className="font-semibold mb-4">Enviar mensaje</h2>
          <div className="grid grid-cols-3 gap-2 mb-4">
            {CHANNELS.map((c) => (
              <button key={c.key} data-testid={`channel-${c.key}`} onClick={() => setChannel(c.key)}
                      className={`rounded-xl border p-3 text-center transition-colors ${channel === c.key ? "border-orange-500 bg-orange-50" : "border-neutral-200 hover:bg-neutral-50"}`}>
                <c.icon size={18} className="mx-auto mb-1 text-orange-600" />
                <p className="text-xs font-medium">{c.label}</p>
              </button>
            ))}
          </div>
          <p className="text-xs text-neutral-500 mb-3">{CHANNELS.find((c) => c.key === channel).desc}</p>
          {channel === "queja" && (
            <label className="flex items-center gap-2 text-sm mb-3">
              <input type="checkbox" data-testid="anonymous-checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} className="accent-orange-600" />
              Enviar de forma anónima
            </label>
          )}
          <Textarea data-testid="message-content-input" rows={4} placeholder="Escribe tu mensaje…" value={content} onChange={(e) => setContent(e.target.value)} />
          <Button data-testid="message-send-btn" onClick={send} className="mt-3 rounded-full bg-orange-600 hover:bg-orange-700 text-white">Enviar</Button>
        </div>

        <div>
          <h2 className="font-semibold mb-4">{isStudent ? "Mis mensajes" : "Mensajes recibidos"}</h2>
          <div className="space-y-3">
            {messages.length === 0 && <p className="text-sm text-neutral-500">No hay mensajes.</p>}
            {messages.map((m) => (
              <div key={m.id} className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-4" data-testid="message-card">
                <p className="text-xs font-semibold text-orange-700 uppercase tracking-wider">
                  {m.channel === "queja" ? "Queja/Sugerencia" : m.channel}
                  <span className="text-neutral-400 font-normal normal-case tracking-normal"> · {m.user_name} · {fmtDate(m.date)}</span>
                </p>
                <p className="text-sm mt-1">{m.content}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
